import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Chip-style tag input with typeahead from the existing-tag list. Prevents the
 * "growth" vs "Growth" drift that you get from a raw comma-separated text
 * input by normalizing on save (lowercase, trimmed, dedup'd) and surfacing
 * existing tags as you type.
 *
 * Submission semantics: parent owns the canonical string[]; this is a
 * controlled component. Hitting Enter or comma commits the current draft as a
 * chip. Backspace on an empty draft removes the last chip.
 */
export function TagInput({
  value,
  onChange,
  suggestions,
  maxTags = 6,
  placeholder = 'Add a tag and press Enter',
}: {
  value: string[];
  onChange: (next: string[]) => void;
  suggestions: string[];
  maxTags?: number;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close the suggestion list on outside click. (Click-inside is preserved so
  // the input keeps focus when the user clicks a suggestion.)
  useEffect(() => {
    if (!showSuggestions) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showSuggestions]);

  const normalized = useMemo(() => value.map((t) => t.toLowerCase()), [value]);

  // Filter suggestions by the current draft, exclude already-selected tags.
  const filtered = useMemo(() => {
    const q = draft.trim().toLowerCase();
    return suggestions
      .filter((s) => !normalized.includes(s.toLowerCase()))
      .filter((s) => q.length === 0 || s.toLowerCase().includes(q))
      .slice(0, 8);
  }, [draft, suggestions, normalized]);

  const commit = (raw: string) => {
    const cleaned = raw.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!cleaned) return;
    if (normalized.includes(cleaned)) {
      setDraft('');
      return;
    }
    if (value.length >= maxTags) return;
    onChange([...value, cleaned]);
    setDraft('');
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && draft.length === 0 && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="input flex flex-wrap items-center gap-1.5">
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-canvas px-2 py-0.5 text-xs font-medium text-ink"
          >
            {tag}
            <button
              type="button"
              onClick={() => onChange(value.filter((t) => t !== tag))}
              className="text-ink-subtle hover:text-ink"
              aria-label={`Remove ${tag}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKey}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] border-0 bg-transparent p-0 text-sm focus:outline-none focus:ring-0"
          disabled={value.length >= maxTags}
        />
      </div>

      {showSuggestions && filtered.length > 0 ? (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-xl border border-line bg-surface py-1 shadow-cardHover">
          {filtered.map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => {
                // mousedown so the click registers before the input blurs.
                e.preventDefault();
                commit(s);
                inputRef.current?.focus();
              }}
              className="block w-full px-3 py-1.5 text-left text-sm hover:bg-canvas"
            >
              {s}
            </button>
          ))}
        </div>
      ) : null}

      {value.length >= maxTags ? (
        <p className="mt-1 text-[11px] text-ink-subtle">Maximum {maxTags} tags.</p>
      ) : null}
    </div>
  );
}
